import { useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { patientService } from '@/services/patientService';
import { useOptionalEncounter } from '@/contexts/EncounterContext';

/**
 * Route pattern: optional practitioner portal prefix followed by /patients/:id.
 * Admin + clinic_staff share the root portal at `/`.
 * Matches:
 *   /patients/123
 *   /practitioner-portal/patients/123
 */
const PATIENT_ROUTE_REGEX = /^(?:\/practitioner-portal)?\/patients\/([^/?]+)/;

export interface PatientRouteContext {
  patientId: string | null;
  patientName: string | null;
  activeTab: string | null;
  previousPatientId: string | null;
  /** Encounter ID when the user is inside the visit cockpit. */
  encounterId: string | null;
}

export function usePatientRouteContext(): PatientRouteContext {
  const location = useLocation();
  // Inside the visit cockpit the URL is /visit/:encounterId, but the
  // EncounterContext exposes both encounter & patient — prefer that when
  // present so the chat FAB scopes to the active visit.
  const encounterCtx = useOptionalEncounter();

  const { patientId: routePatientId, activeTab } = useMemo(() => {
    const match = location.pathname.match(PATIENT_ROUTE_REGEX);
    const id = match?.[1] ?? null;
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    return { patientId: id, activeTab: tab };
  }, [location.pathname, location.search]);

  const patientId = encounterCtx?.patientId ?? routePatientId;
  const encounterId = encounterCtx?.encounterId ?? null;

  // Track previous patient ID for context-change detection (R9)
  const previousPatientIdRef = useRef<string | null>(null);
  const previousPatientId = previousPatientIdRef.current;

  // Update the ref after reading it so consumers see the prior value
  if (patientId !== previousPatientIdRef.current) {
    previousPatientIdRef.current = patientId;
  }

  // Fetch patient name when we have an ID (cached via TanStack Query)
  const { data: patientData } = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => patientService.getPatientById(patientId!),
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const patientName = useMemo(() => {
    if (!patientData?.data) return null;
    const p = patientData.data;
    return [p.first_name, p.last_name].filter(Boolean).join(' ') || null;
  }, [patientData]);

  return { patientId, patientName, activeTab, previousPatientId, encounterId };
}
